/**
 * FilterDrawer styles
 *
 * Top drawer with handle containing on/off toggle.
 * Toggle works whether drawer is open or closed.
 */
export const filterDrawerStyles = `
/* Filter Wrapper - fixed position container */
.filter-wrapper {
  position: fixed;
  top: var(--header-height, 60px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 800;
  display: flex;
  flex-direction: column;
  align-items: center;
  pointer-events: none; /* Don't block map interactions */
}

/* Handle - always visible at bottom */
.filter-handle {
  background: rgba(255, 255, 255, 0.98);
  padding: 6px 14px;
  border-radius: 0 0 8px 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  border-top: 2px solid rgba(22, 163, 74, 0.5);
  box-shadow:
    0 2px 10px rgba(22, 163, 74, 0.25),
    0 0 12px rgba(22, 163, 74, 0.2);
  transition: all 0.2s;
  order: 1; /* Handle below panel */
  pointer-events: auto; /* Handle is always clickable */
}

.filter-handle:hover {
  padding-bottom: 10px;
  box-shadow:
    0 4px 15px rgba(22, 163, 74, 0.35),
    0 0 16px rgba(22, 163, 74, 0.3);
  border-top-color: rgba(22, 163, 74, 0.7);
}

.filter-handle-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: #333;
  pointer-events: none;
}

/* Points down (toward the panel that's about to drop below it) when
   closed, flips to point up (toward the handle's own resting position)
   when open - the transition is what makes the flip itself readable as
   "this control just changed state," not just the end position. */
.filter-handle-arrow {
  flex-shrink: 0;
  transition: transform 0.2s ease;
  pointer-events: none;
}

.filter-wrapper.open .filter-handle-arrow {
  transform: rotate(180deg);
}

/* Toggle switch in handle */
.filter-handle-toggle {
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-left: 4px;
}

.filter-handle-toggle input {
  display: none;
}

.filter-handle-slider {
  width: 32px;
  height: 18px;
  background: #ccc;
  border-radius: 9px;
  position: relative;
  transition: background 0.2s;
}

.filter-handle-slider::after {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  width: 14px;
  height: 14px;
  background: white;
  border-radius: 50%;
  transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.filter-handle-toggle input:checked + .filter-handle-slider {
  background: #16a34a;
}

.filter-handle-toggle input:checked + .filter-handle-slider::after {
  transform: translateX(14px);
}

/* Disabled = nothing to clear (all-defaults) - not independently
   switchable on, since there's no criteria to turn "on" with a click. */
.filter-handle-toggle input:disabled + .filter-handle-slider {
  opacity: 0.5;
  cursor: not-allowed;
}

.filter-handle-toggle:has(input:disabled) {
  cursor: not-allowed;
}

/* Ask result toggle - shares the same pill mechanics, purple accent */
.filter-handle-divider {
  width: 1px;
  height: 16px;
  background: rgba(0, 0, 0, 0.15);
}

.filter-handle-label-ask {
  font-size: 0.75rem;
  font-weight: 500;
  color: #7c3aed;
  pointer-events: none;
}

.filter-handle-toggle-ask input:checked + .filter-handle-slider {
  background: #7c3aed;
}

/* Panel - slides down from top */
.filter-panel {
  background: white;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  border-radius: 0 0 12px 12px;
  max-height: 0;
  overflow: hidden;
  opacity: 0;
  transition: max-height 0.3s ease, opacity 0.2s ease;
  order: 0; /* Panel above handle */
}

.filter-wrapper.open .filter-panel {
  /* Fixed max-height (not height:auto) so it can transition - was a flat
     400px, sized for the original 5 rows (Price, Beds/Baths, Type, STR,
     Actions). Sqft/Year Built/Days on Market/Keyword pushed real content
     height past that, clipping Keyword and Clear entirely with no way to
     reach them. min(...) keeps it from overflowing a short viewport
     instead of just raising the flat number again; overflow-y:auto is the
     safety net if it still doesn't fit. */
  max-height: min(640px, 85vh);
  opacity: 1;
  pointer-events: auto; /* Panel is interactive when open */
  overflow-y: auto;
  overflow-x: hidden;
}

.filter-panel-body {
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.filter-row {
  display: flex;
  gap: 12px;
  align-items: flex-end;
}

.filter-section {
  min-width: 70px;
  flex: 1;
}

.filter-section-price {
  flex: 1;
  min-width: 90px;
}

.filter-section-wide {
  flex: 2;
  min-width: 200px;
}

.filter-section-label {
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #666;
  margin-bottom: 4px;
}

.filter-select {
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.8rem;
  background: white;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.filter-select:focus {
  outline: none;
  border-color: #16a34a;
  box-shadow: 0 0 0 2px rgba(22, 163, 74, 0.1);
}

.filter-input {
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.8rem;
  background: white;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.filter-input:focus {
  outline: none;
  border-color: #16a34a;
  box-shadow: 0 0 0 2px rgba(22, 163, 74, 0.1);
}

.filter-input::placeholder {
  color: #9ca3af;
}

/* Multi-select chips */
.filter-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.filter-chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border: 1px solid #d1d5db;
  border-radius: 16px;
  font-size: 0.75rem;
  color: #666;
  cursor: pointer;
  transition: all 0.15s;
  user-select: none;
}

.filter-chip:hover {
  border-color: #16a34a;
  color: #16a34a;
}

.filter-chip.active {
  background: #dcfce7;
  border-color: #22c55e;
  color: #166534;
}

.filter-chip input {
  display: none;
}

/* Actions */
.filter-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 4px;
  padding-top: 8px;
  border-top: 1px solid #e5e7eb;
}

.filter-clear-btn {
  padding: 6px 14px;
  background: white;
  color: #666;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-weight: 500;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
}

.filter-clear-btn:hover {
  background: #f3f4f6;
  border-color: #9ca3af;
  color: #333;
}

@media (max-width: 767px) {
  .filter-panel-body {
    padding: 10px 12px;
  }

  .filter-row {
    flex-wrap: wrap;
    gap: 8px;
  }

  .filter-section {
    flex: 1 1 45%;
    min-width: 80px;
  }

  .filter-section-price {
    flex: 1 1 45%;
  }

  .filter-section-wide {
    flex: 1 1 100%;
    min-width: 100%;
  }

  .filter-actions {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .filter-handle {
    padding: 5px 10px;
    gap: 6px;
  }

  .filter-handle-label {
    font-size: 0.7rem;
  }

  .filter-handle-slider {
    width: 28px;
    height: 16px;
  }

  .filter-handle-slider::after {
    width: 12px;
    height: 12px;
  }

  .filter-handle-toggle input:checked + .filter-handle-slider::after {
    transform: translateX(12px);
  }
}

/* Touch device improvements */
@media (hover: none) and (pointer: coarse) {
  .filter-handle {
    min-height: 44px;
  }

  .filter-chip {
    min-height: 40px;
    padding: 8px 14px;
  }

  .filter-input,
  .filter-select {
    min-height: 44px;
    font-size: 16px; /* Prevents iOS zoom */
  }

  .filter-clear-btn {
    min-height: 44px;
    padding: 10px 20px;
  }
}
`;
export function injectStyles() {
    if (document.getElementById('filter-drawer-styles'))
        return;
    const style = document.createElement('style');
    style.id = 'filter-drawer-styles';
    style.textContent = filterDrawerStyles;
    document.head.appendChild(style);
}
//# sourceMappingURL=styles.js.map